// スロット / 中継点の一括平行移動のプランニング（純関数）。
//
// 対象ポイント（記録ルートの端点スロット *r<mapId>/rec-1|rec-2、および
// 中継点ステーション *r<mapId>/rec-<i>-<j>）をルートの対応マークへ解決し、
// ルートごとに移動後のマーク列を作る。実際の保存は再記録方式
// （useScenarios の applySlotMoveBulk → useRouteEdit の reRecordRoute）で行う。
//
// - 選択ポイントと同一位置（シナリオ接続判定と同じ DIST_THRESHOLD 以内）にある
//   他ルートの端点・中継点も一緒に移動し、シナリオのルート間接続を保つ。
// - 移動するのは選択（と同一位置展開）で対象になったマークのみ。ルート全体を
//   平行移動したい場合は矩形選択で端点・中継点をまとめて選択する。

import type { EditableMark, RouteMap } from '@/types'
import { DIST_THRESHOLD } from '@/types'
import { extractEditableMarks, validateMarks } from './markUtil'

export interface SlotMoveJob {
  oldMapId: string
  marks: EditableMark[]
}

export type SlotMovePlan =
  | { ok: true; jobs: SlotMoveJob[]; pointCount: number }
  | { ok: false; reason: 'noTarget' }
  | { ok: false; reason: 'extractFailed' | 'validation'; mapId: string }

// 記録ルートの端点スロット ID
const SLOT_ID_RE = /^\*r([^/]+)\/(rec-[12])$/
// 記録ルートの中継点ステーション ID（rec-<マーク番号>-<隣接マーク番号>）
const WAYPOINT_ID_RE = /^\*r([^/]+)\/rec-(\d+)-(\d+)$/

// ドラッグで平行移動できるポイント（記録ルートの端点スロット / 中継点）か
export function isMovablePointId(id: string): boolean {
  return SLOT_ID_RE.test(id) || WAYPOINT_ID_RE.test(id)
}

// 移動候補: ルート (mapId) のどのマークか（端点 'start'/'end' または中継マーク番号）
interface MoveCandidate {
  mapId: string
  target: 'start' | 'end' | number
  x: number
  y: number
}

// マップ内の全移動候補（全ルートの端点スロット + 中継点ステーション）を集める
function collectCandidates(map: RouteMap): MoveCandidate[] {
  const out: MoveCandidate[] = []
  for (const slot of map.slots.values()) {
    const m = slot.id.match(SLOT_ID_RE)
    if (!m) continue
    const st = map.stations.get(slot.stationId)
    if (!st?.pose) continue
    out.push({
      mapId: m[1],
      target: m[2] === 'rec-1' ? 'start' : 'end',
      x: st.pose.x,
      y: st.pose.y,
    })
  }
  for (const st of map.stations.values()) {
    const m = st.id.match(WAYPOINT_ID_RE)
    if (!m || !st.pose) continue
    out.push({
      mapId: m[1],
      target: Number(m[2]),
      x: st.pose.x,
      y: st.pose.y,
    })
  }
  return out
}

export function planSlotMove(
  map: RouteMap,
  pointIds: string[],
  delta: { x: number; y: number }
): SlotMovePlan {
  // 選択ポイント（スロット / 中継点ステーション）の位置を集める
  const seedPositions: { x: number; y: number }[] = []
  for (const id of pointIds) {
    if (SLOT_ID_RE.test(id)) {
      const slot = map.slots.get(id)
      const st = slot && map.stations.get(slot.stationId)
      if (st?.pose) seedPositions.push(st.pose)
    } else if (WAYPOINT_ID_RE.test(id)) {
      const st = map.stations.get(id)
      if (st?.pose) seedPositions.push(st.pose)
    }
  }
  if (seedPositions.length === 0) return { ok: false, reason: 'noTarget' }

  // 同一位置のポイントも対象に含め、mapId ごとに対象マークをグループ化する
  const byRoute = new Map<string, Set<'start' | 'end' | number>>()
  for (const c of collectCandidates(map)) {
    const near = seedPositions.some(
      (p) => Math.abs(p.x - c.x) + Math.abs(p.y - c.y) <= DIST_THRESHOLD
    )
    if (!near) continue
    const targets = byRoute.get(c.mapId) ?? new Set<'start' | 'end' | number>()
    targets.add(c.target)
    byRoute.set(c.mapId, targets)
  }

  const jobs: SlotMoveJob[] = []
  let pointCount = 0
  for (const [mapId, targets] of byRoute) {
    const marks = extractEditableMarks(map, mapId)
    if (!marks) return { ok: false, reason: 'extractFailed', mapId }
    // 端点指定・中継マーク番号を実際のマークインデックスへ解決する
    // （端点マークは中継点ステーション経由でも同じインデックスになる）
    const idxs = new Set<number>()
    for (const t of targets) {
      if (t === 'start') idxs.add(0)
      else if (t === 'end') idxs.add(marks.length - 1)
      else if (t >= 0 && t < marks.length) idxs.add(t)
    }
    if (idxs.size === 0) continue
    pointCount += idxs.size
    const shifted = marks.map((mark, i) =>
      idxs.has(i) ? { ...mark, x: mark.x + delta.x, y: mark.y + delta.y } : mark
    )
    if (validateMarks(shifted).length > 0) {
      return { ok: false, reason: 'validation', mapId }
    }
    jobs.push({ oldMapId: mapId, marks: shifted })
  }

  return { ok: true, jobs, pointCount }
}
